import { expandEllipses } from "./series";
import { FORMULA_LINE, diagramBlockEnd, readMeta, type TableMeta } from "./tableFormulas";

/**
 * Visueller Tabellen-Editor: bearbeitet ein 2D-Zellenraster (erste Zeile =
 * Kopf) und serialisiert nach Markdown. Kein |---|-Handarbeit nötig.
 */

export interface TableBlock {
  start: number; // Zeichenindex Blockanfang
  end: number; // Zeichenindex Blockende (exklusiv)
  cells: string[][];
  /** Formeln, Formate, Diagramm aus dem visuellen Editor (tableFormulas.ts). */
  meta: TableMeta;
  /** Ende inklusive Formelzeile und zugehörigem Diagramm darunter (sonst = `end`). */
  endWithFormulas: number;
}

/** Trennzeilen-Zelle: `---`, `:-:`, aber auch ein einzelnes `-` (GFM erlaubt das). */
export const SEP_CELL = /^\s*:?-+:?\s*$/;

/**
 * Findet die Markdown-Tabelle GENAU an der Cursorposition (oder null).
 *
 * Bewusst streng, nicht "nächstgelegene Tabelle im Dokument" – eine frühere
 * Fassung (`findTableNear`) griff bei jedem Aufruf des Tabellen-Knopfs auf
 * die nächste Tabelle irgendwo im Text zurück, sobald der Cursor nicht exakt
 * darin stand. Das überraschte: an einer leeren Stelle "/tabelle" zu tippen
 * bearbeitete dann eine bestehende Tabelle, statt eine neue anzulegen.
 * Bearbeiten soll nur passieren, wenn der Cursor wirklich in einer Tabelle
 * steht – sonst wird eine neue eingefügt.
 */
export function findTableAt(text: string, pos: number): TableBlock | null {
  const lines = text.split("\n");
  // Zeilenindex der Cursorposition bestimmen
  let acc = 0;
  let lineIdx = 0;
  for (let i = 0; i < lines.length; i++) {
    if (pos <= acc + lines[i].length) {
      lineIdx = i;
      break;
    }
    acc += lines[i].length + 1;
  }
  const isTableLine = (s: string) => /^\s*\|/.test(s);
  // Cursor auf der Formelzeile unter der Tabelle zählt als „in der Tabelle" –
  // sonst öffnete der Tabellen-Knopf dort einen leeren Editor.
  if (FORMULA_LINE.test((lines[lineIdx] ?? "").trim()) && isTableLine(lines[lineIdx - 1] ?? "")) lineIdx--;
  if (!isTableLine(lines[lineIdx] ?? "")) return null;

  let first = lineIdx;
  while (first > 0 && isTableLine(lines[first - 1])) first--;
  let last = lineIdx;
  while (last < lines.length - 1 && isTableLine(lines[last + 1])) last++;

  const start = lines.slice(0, first).reduce((n, l) => n + l.length + 1, 0);
  const end = lines.slice(0, last + 1).reduce((n, l) => n + l.length + 1, 0) - 1;

  const cells: string[][] = [];
  let hasSeparator = false;
  for (let i = first; i <= last; i++) {
    const row = splitRow(lines[i]);
    if (row.length > 1 && row.every((c) => SEP_CELL.test(c))) {
      hasSeparator = true;
      continue; // Trennzeile überspringen
    }
    cells.push(row);
  }
  // Ohne echte Kopf-Trennzeile (|---|---|) ist es keine Tabelle, sondern nur
  // eine Zeile, die zufällig mit `|` anfängt – z. B. der Spalten-Trenner `|||`
  // oder ein Inline-Spoiler `||Text||`, der ebenfalls mit `|` beginnt. Beide
  // sonst irrtümlich als 1-Zeilen-„Tabelle" geöffnet.
  if (!cells.length || !hasSeparator) return null;
  const next = lines[last + 1] ?? "";
  const hasMeta = FORMULA_LINE.test(next.trim());
  let lastExtra = hasMeta ? last + 1 : last;
  const diagramEnd = hasMeta ? diagramBlockEnd(lines, last + 2) : null;
  if (diagramEnd != null) lastExtra = diagramEnd - 1;
  const endWithFormulas = lines.slice(0, lastExtra + 1).reduce((n, l) => n + l.length + 1, 0) - 1;
  return { start, end, cells, meta: hasMeta ? readMeta(next) : { formulas: {}, formats: {} }, endWithFormulas };
}

/** Zellen einer Tabellenzeile `| a | b |` (maskierte `\|` bleiben Teil der Zelle). */
export function splitRow(line: string): string[] {
  const raw = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return raw.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

/**
 * `...` in den Zeilen ausfüllen (series.ts). Die Kopfzeile braucht dafür ein
 * Endglied (`2 | ... | 12`), die übrigen Zeilen werden ohne Endglied bis zur
 * Spaltenzahl der Kopfzeile aufgefüllt. `null`, wenn es nichts auszufüllen gab.
 */
export function expandTableRows(cells: string[][]): string[][] | null {
  let changed = false;
  const head = expandEllipses(cells[0] ?? []);
  const out = [head ?? cells[0]];
  if (head) changed = true;
  for (const row of cells.slice(1)) {
    const next = expandEllipses(row, out[0].length);
    if (next) changed = true;
    out.push(next ?? row);
  }
  return changed ? out : null;
}

/**
 * Baut Markdown aus dem Zellenraster – bewusst mit nur EINEM Leerzeichen je
 * Zelle, egal wie breit der Inhalt ist. Ausgerichtet sieht die Tabelle trotzdem
 * aus: das übernimmt rein optisch der Code-Editor (siehe tableAlign.ts), die
 * gespeicherte Datei bleibt dadurch kurz und lesbar als Diff.
 */
export function serializeTable(cells: string[][]): string {
  const cols = Math.max(...cells.map((r) => r.length), 1);
  const rows = cells.map((r) =>
    Array.from({ length: cols }, (_, i) => (r[i] ?? "").replace(/\|/g, "\\|").trim())
  );
  const line = (r: string[]) => `| ${r.join(" | ")} |`;
  return [
    line(rows[0] ?? Array(cols).fill("")),
    `| ${Array(cols).fill("---").join(" | ")} |`,
    ...rows.slice(1).map(line),
  ].join("\n");
}

/**
 * Aus Excel/Numbers/Google Tabellen kopiert kommt Text mit Tabulatoren
 * zwischen den Zellen – daraus direkt eine Markdown-Tabelle. Nur wenn es
 * wirklich wie eine Tabelle aussieht (jede Zeile gleich viele Tabulatoren,
 * keine am Zeilenanfang): eingefügter Code mit Tab-Einrückung bleibt Code.
 */
export function tsvToTable(text: string): string | null {
  const lines = text.replace(/\r\n?/g, "\n").replace(/\n+$/, "").split("\n");
  const tabs = (l: string) => (l.match(/\t/g) ?? []).length;
  if (!lines.length || tabs(lines[0]) < 1) return null;
  if (lines.some((l) => l.startsWith("\t") || tabs(l) !== tabs(lines[0]))) return null;
  return serializeTable(lines.map((l) => l.split("\t")));
}

/**
 * Öffnet den visuellen Editor (kleine Tabellenkalkulation, sheetEditor.ts –
 * erst hier nachgeladen). onSave erhält fertiges Markdown, bei Formeln samt
 * unsichtbarer Formelzeile darunter.
 */
export function openTableEditor(opts: {
  initial?: string[][];
  meta?: TableMeta;
  onSave: (markdown: string) => void;
}): void {
  void import("./sheetEditor").then(({ openSheetEditor }) =>
    openSheetEditor({
      ...opts,
      // `...` in einer Zelle wird auch hier ausgefüllt (siehe expandTableRows).
      serialize: (cells) => serializeTable(expandTableRows(cells) ?? cells),
    })
  );
}
